import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Plane, Hotel, FileText, Loader2, Plus, Upload, Trash2, Download, PlaneLanding, PlaneTakeoff,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

export default function Travel() {
  const { can } = useAuth();
  const t = useT();
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [flights, setFlights] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [flightOpen, setFlightOpen] = useState(false);
  const [hotelOpen, setHotelOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const canCreate = can("travel", "create");
  const canEdit = can("travel", "edit");

  useEffect(() => {
    api.get("/patients").then((r) => {
      setPatients(r.data);
      if (r.data.length) setPatientId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    Promise.all([
      api.get(`/travel/flights?patient_id=${patientId}`),
      api.get(`/travel/hotels?patient_id=${patientId}`),
      api.get(`/travel/documents?patient_id=${patientId}`),
    ]).then(([f, h, d]) => {
      setFlights(f.data); setHotels(h.data); setDocs(d.data);
    }).finally(() => setLoading(false));
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !patientId) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("patient_id", patientId);
    fd.append("kind", "travel_document");
    fd.append("file", file);
    try {
      await api.post("/travel/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded securely");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const viewDoc = async (d) => {
    try {
      const res = await api.get(`/travel/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Unable to open document");
    }
  };

  const removeDoc = async (d) => {
    try { await api.delete(`/travel/documents/${d.id}`); toast.success("Document removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delFlight = async (f) => {
    try { await api.delete(`/travel/flights/${f.id}`); toast.success("Flight removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delHotel = async (h) => {
    try { await api.delete(`/travel/hotels/${h.id}`); toast.success("Hotel removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("travel.title")} subtitle={t("travel.subtitle")}>
        <Select value={patientId} onValueChange={setPatientId}>
          <SelectTrigger className="w-64" data-testid="travel-patient-select"><SelectValue placeholder={t("travel.selectPatient")} /></SelectTrigger>
          <SelectContent>
            {patients.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.patient_code} · {p.first_name} {p.last_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageHeader>

      {loading ? (
        <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Flights */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-medium text-navy flex items-center gap-2"><Plane className="h-4 w-4" /> {t("travel.flights")}</h3>
              {canCreate && (
                <FlightDialog open={flightOpen} setOpen={setFlightOpen} patientId={patientId} onDone={load} />
              )}
            </div>
            {flights.length === 0 ? <Empty text={t("travel.noFlights")} /> : (
              <div className="space-y-3" data-testid="flight-list">
                {flights.map((f) => (
                  <div key={f.id} data-testid={`flight-${f.id}`} className="rounded-lg border border-slate-100 p-3 flex items-start gap-3">
                    {f.direction === "arrival"
                      ? <PlaneLanding className="h-5 w-5 text-emerald-600 mt-0.5" />
                      : <PlaneTakeoff className="h-5 w-5 text-blue-600 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{t(`travel.${f.direction}`, f.direction)}</span>
                        <Badge variant="secondary">{f.airline} {f.flight_number}</Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{f.departure_airport} → {f.arrival_airport} · {fmt(f.scheduled_time)}</p>
                      {f.terminal && <p className="text-xs text-slate-400">{t("travel.terminal")}: {f.terminal}</p>}
                    </div>
                    {canEdit && <button onClick={() => delFlight(f)} data-testid={`del-flight-${f.id}`} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Hotels */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-medium text-navy flex items-center gap-2"><Hotel className="h-4 w-4" /> {t("travel.hotels")}</h3>
              {canCreate && (
                <HotelDialog open={hotelOpen} setOpen={setHotelOpen} patientId={patientId} onDone={load} />
              )}
            </div>
            {hotels.length === 0 ? <Empty text={t("travel.noHotels")} /> : (
              <div className="space-y-3" data-testid="hotel-list">
                {hotels.map((h) => (
                  <div key={h.id} data-testid={`hotel-${h.id}`} className="rounded-lg border border-slate-100 p-3 flex items-start gap-3">
                    <Hotel className="h-5 w-5 text-navy mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{h.hotel_name}</p>
                      <p className="text-sm text-slate-500">{h.address}{h.room_number ? ` · ${t("travel.room")} ${h.room_number}` : ""}</p>
                      <p className="text-xs text-slate-400">{fmtDate(h.check_in)} → {fmtDate(h.check_out)}{h.confirmation_number ? ` · #${h.confirmation_number}` : ""}</p>
                    </div>
                    {canEdit && <button onClick={() => delHotel(h)} data-testid={`del-hotel-${h.id}`} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Documents */}
          <section className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-medium text-navy flex items-center gap-2"><FileText className="h-4 w-4" /> {t("travel.documents")} <span className="text-xs font-normal text-slate-400">{t("travel.docsHint")}</span></h3>
              {canCreate && (
                <>
                  <input ref={fileRef} type="file" className="hidden" onChange={uploadDoc}
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" data-testid="doc-file-input" />
                  <Button size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}
                    data-testid="upload-doc-button" className="bg-navy hover:bg-navy-soft text-white gap-2">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("travel.upload")}
                  </Button>
                </>
              )}
            </div>
            {docs.length === 0 ? <Empty text={t("travel.noDocs")} /> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="doc-list">
                {docs.map((d) => (
                  <div key={d.id} data-testid={`doc-${d.id}`} className="rounded-lg border border-slate-100 p-3 flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs text-slate-400">{(d.size / 1024).toFixed(0)} KB · {d.uploaded_by_name}</p>
                    </div>
                    <button onClick={() => viewDoc(d)} data-testid={`view-doc-${d.id}`} className="text-slate-400 hover:text-navy"><Download className="h-4 w-4" /></button>
                    {canEdit && <button onClick={() => removeDoc(d)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-center text-sm text-slate-400 py-8">{text}</p>;
}

function FlightDialog({ open, setOpen, patientId, onDone }) {
  const t = useT();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ direction: "arrival", airline: "", flight_number: "", departure_airport: "", arrival_airport: "", date: "", time: "12:00", terminal: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.date) return toast.error(t("travel.selectDate"));
    setSaving(true);
    try {
      await api.post("/travel/flights", {
        patient_id: patientId, direction: f.direction, airline: f.airline,
        flight_number: f.flight_number, departure_airport: f.departure_airport,
        arrival_airport: f.arrival_airport, scheduled_time: `${f.date}T${f.time}:00`, terminal: f.terminal,
      });
      toast.success(t("travel.flightAdded"));
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-flight-button" className="gap-1"><Plus className="h-4 w-4" /> {t("travel.add")}</Button></DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{t("travel.addFlight")}</DialogTitle><DialogDescription>{t("travel.addFlightDesc")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2" data-testid="flight-form">
          <div className="space-y-1.5 col-span-2">
            <Label>{t("travel.direction")}</Label>
            <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
              <SelectTrigger data-testid="flight-direction"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="arrival">{t("travel.arrival")}</SelectItem><SelectItem value="departure">{t("travel.departure")}</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>{t("travel.airline")}</Label><Input value={f.airline} onChange={(e) => setF({ ...f, airline: e.target.value })} data-testid="flight-airline" /></div>
          <div className="space-y-1.5"><Label>{t("travel.flightNo")}</Label><Input value={f.flight_number} onChange={(e) => setF({ ...f, flight_number: e.target.value })} data-testid="flight-number" /></div>
          <div className="space-y-1.5"><Label>{t("travel.fromIata")}</Label><Input value={f.departure_airport} onChange={(e) => setF({ ...f, departure_airport: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>{t("travel.toIata")}</Label><Input value={f.arrival_airport} onChange={(e) => setF({ ...f, arrival_airport: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>{t("travel.addFlight") && t("transport.date")}</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="flight-date" /></div>
          <div className="space-y-1.5"><Label>{t("travel.time")}</Label><Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>
          <div className="space-y-1.5 col-span-2"><Label>{t("travel.terminal")}</Label><Input value={f.terminal} onChange={(e) => setF({ ...f, terminal: e.target.value })} /></div>
          <DialogFooter className="col-span-2 mt-2">
            <Button type="submit" disabled={saving} data-testid="flight-submit" className="bg-navy hover:bg-navy-soft text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("travel.addFlightBtn")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HotelDialog({ open, setOpen, patientId, onDone }) {
  const t = useT();
  const [saving, setSaving] = useState(false);
  const [h, setH] = useState({ hotel_name: "", address: "", room_number: "", check_in: "", check_out: "", confirmation_number: "", phone: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!h.hotel_name) return toast.error(t("travel.hotelNameReq"));
    setSaving(true);
    try {
      await api.post("/travel/hotels", { patient_id: patientId, ...h });
      toast.success(t("travel.hotelAdded"));
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-hotel-button" className="gap-1"><Plus className="h-4 w-4" /> {t("travel.add")}</Button></DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{t("travel.addHotel")}</DialogTitle><DialogDescription>{t("travel.addHotelDesc")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2" data-testid="hotel-form">
          <div className="space-y-1.5 col-span-2"><Label>{t("travel.hotelName")} *</Label><Input value={h.hotel_name} onChange={(e) => setH({ ...h, hotel_name: e.target.value })} data-testid="hotel-name" /></div>
          <div className="space-y-1.5 col-span-2"><Label>{t("travel.address")}</Label><Input value={h.address} onChange={(e) => setH({ ...h, address: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>{t("travel.room")}</Label><Input value={h.room_number} onChange={(e) => setH({ ...h, room_number: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>{t("travel.confirmation")}</Label><Input value={h.confirmation_number} onChange={(e) => setH({ ...h, confirmation_number: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>{t("travel.checkIn")}</Label><Input type="date" value={h.check_in} onChange={(e) => setH({ ...h, check_in: e.target.value })} data-testid="hotel-checkin" /></div>
          <div className="space-y-1.5"><Label>{t("travel.checkOut")}</Label><Input type="date" value={h.check_out} onChange={(e) => setH({ ...h, check_out: e.target.value })} /></div>
          <div className="space-y-1.5 col-span-2"><Label>{t("travel.phone")}</Label><Input value={h.phone} onChange={(e) => setH({ ...h, phone: e.target.value })} /></div>
          <DialogFooter className="col-span-2 mt-2">
            <Button type="submit" disabled={saving} data-testid="hotel-submit" className="bg-navy hover:bg-navy-soft text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("travel.addHotelBtn")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
